<template>
  <div
    ref="pdfOutputArea"
    class="pdf-output-area"
    :class="{ 'is-measuring': billing && !paginationReady }"
    :data-pdf-ready="paginationReady ? 'true' : 'false'"
  >
    <div ref="pdfContent">
      <div ref="continuationHeaderProbe" class="text-end me-2 measurement-probe">2/2</div>
      <div v-if="billing">
        <div
          v-for="(page, pageIndex) in paginatedBilling"
          :key="pageIndex"
          class="page"
        >
          <div 
            class="list-container" 
            :class="{ 'page-break': pageIndex !== 0 }"
          >
            <!-- ✅ ヘッダー部分（1ページ目のみ表示） -->
            <div v-if="pageIndex === 0" class="header">

              <div class="d-flex justify-content-between">
                <div class="one-third-block"></div>
                <h1>請求書</h1>
                <div class="one-third-block">
                  <div v-if="billing.billing_dt_display_flg === true">
                    <p class="biiling-dt mb-0 text-end pe-0">{{ formatYearMonthDay(billing.billing_dt) }}</p>
                  </div>
                  <h5 class="text-end">請求番号 {{ billing.parent_billing_no }}</h5>
                  <div class="text-end me-2">{{ pageIndex + 1 }}/{{ paginatedBilling.length }}</div>
                </div>
              </div>

              <div class="d-flex justify-content-between mb-2">
                <!-- <h4 class="billing-dear-nm mb-0 align-self-start">{{ billing.dear_nm }}御中</h4> -->
                <h4 class="billing-dear-nm mb-0 align-self-start">
                  <span class="multiline-text">{{ billing.m_customer?.customer_nm }} {{ billing.honorific_title }}</span>
                </h4>
              </div>
              <div class="middle-title d-flex justify-content-between" style="position: relative">
                <img
                  v-if="billing.seal_flg"
                  width="60"
                  height="60"
                  src="../../../../assets/billing_sign_img.png"
                  style="cursor: pointer; position: absolute; top: -66px; right: 60px;"
                >
                <h4 class="billing-greeting mb-1 text-nowrap">下記の通り御請求申し上げます。</h4>
                <div class="text-end">
                  <div class="company-info-block">
                    <h4 class="billing-company-nm">
                      <span style="margin-right: 12px;">株</span>
                      <span style="margin-right: 12px;">式</span>
                      <span style="margin-right: 12px;">会</span>
                      <span style="margin-right: 28px;">社</span>
                      <span style="margin-right: 12px;">創</span>
                      <span style="margin-right: 12px;">英</span>
                      <span style="margin-right: 12px;">企</span>
                      <span>画</span>
                    </h4>
                    <div v-if="billing.representative_nm_display_flg === true">
                      <h6 class="billing-company-representative">
                        <span class="me-2">代</span>
                        <span class="me-2">表</span>
                        <span class="me-2">取</span>
                        <span class="me-2">締</span>
                        <span style="margin-right: 20px;">役</span>
                        <span class="me-2">鈴</span>
                        <span class="me-2">木</span>
                        <span>明</span>
                      </h6>
                    </div>
                    <p class="billing-company-address"><span class="me-3">〒 151-0053</span><span>東京都渋谷区代々木2-38-6</span></p>
                    <p class="billing-company-address"><span class="me-3">TEL 03-3370-8006</span><span>FAX 3370-8157</span></p>
                  </div>
                  <span class="company-regist-no">登録番号 T7011001012662</span>
                </div>
              </div>
            </div>

            <!-- ✅ ヘッダー部分（1ページ目のみ表示） -->
            <div v-if="pageIndex === 0" class="total-billing-amount-area mb-2">
              <div>
                <h1 class="total-billing-amount">合計金額 {{ formatCurrency(calculateGrandTotal()) }}</h1>
              </div>
              <div v-if="billing.carryover_amount > 0" class="d-flex align-items-center carryover-amount">
                <h5 class="mb-0">繰越金額 {{ formatCurrencyZero(billing.carryover_amount) }}</h5>
              </div>
            </div>

            <!-- ✅ ページ番号（2ページ以降は単体で表示） -->
            <div v-if="pageIndex > 0" class="text-end me-2">{{ pageIndex + 1 }}/{{ paginatedBilling.length }}</div>

            <!-- 業務内容のテーブル -->
            <table class="table monthly-detail-data">
              <thead>
                <tr>
                  <th class="month-data-header text-nowrap">請求ID</th>
                  <th class="month-data-header text-nowrap">建物</th>
                  <th class="month-data-header text-nowrap">月</th>
                  <th class="month-data-header text-nowrap">日</th>
                  <th class="month-data-header text-nowrap">摘要</th>
                  <th v-if="showBreakdown" class="month-data-header text-nowrap">内訳</th>
                  <th class="month-data-header text-nowrap">金額</th>
                </tr>
              </thead>
              <tbody>
                <!-- ▼ 各ページ内の billing 行をループ -->
                <tr
                  v-for="(billingItem, scIndex) in page"
                  :key="scIndex"
                  class="billing-detail-row"
                  :data-detail-index="scIndex"
                >
                  
                  <!-- 請求ID -->
                  <td>{{ billingItem.billing_no }}</td>

                  <!-- ▼ 建物列 -->
                  <template v-if="!billingItem || Object.keys(billingItem).length === 0">
                    <!-- ✅ 空行の場合（ページ補完行）：建物列を空で表示 -->
                    <td class="text-start">&nbsp;</td>
                  </template>

                  <template v-else-if="billingItem.work_type === 2">
                    <!-- ✅ 追加行はrowspanに含めず常に表示 -->
                    <td class="text-start">
                      {{ billingItem.m_building?.building_nm || '' }}
                    </td>
                  </template>

                  <template
                    v-else-if="groupBuildingsInPage(page)[scIndex]"
                  >
                    <!-- ✅ 初期データで、建物の最初の出現位置だけ rowspan 表示 -->
                    <td
                      class="text-start building-nm-cell"
                      :rowspan="groupBuildingsInPage(page)[scIndex].count"
                    >
                      {{ billingItem.m_building?.building_nm || '' }}
                    </td>
                  </template>

                  <!-- ✅ rowspan 対象の2行目以降は建物列なし -->

                  <!-- 作業年月 -->
                  <td>{{ billingItem.work_month }}</td>

                  <!-- 作業日（formatDay関数で整形） -->
                  <td>{{ formatDay(billingItem.work_dt) }}</td>

                  <!-- 摘要 -->
                  <td class="multiline-text text-start">{{ billingItem.work_content }}</td>

                  <!-- 内訳 -->
                  <td v-if="showBreakdown" class="multiline-text text-start">{{ billingItem.breakdown }}</td>

                  <!-- 金額（右寄せ通貨） -->
                  <td class="text-end">{{ formatCurrency(billingItem.bill_amount) }}</td>
                </tr>

                <tr
                  v-if="(fillerHeightByPage[pageIndex] || 0) > 0"
                  class="billing-filler-row"
                  :style="{ height: `${fillerHeightByPage[pageIndex]}px` }"
                >
                  <td class="filler-grid-cell">&nbsp;</td>
                  <td class="filler-grid-cell text-start">&nbsp;</td>
                  <td class="filler-grid-cell">&nbsp;</td>
                  <td class="filler-grid-cell">&nbsp;</td>
                  <td class="filler-grid-cell">&nbsp;</td>
                  <td v-if="showBreakdown" class="filler-grid-cell">&nbsp;</td>
                  <td class="filler-grid-cell">&nbsp;</td>
                </tr>

                <!-- ▼ 最終ページなら小計・税・合計を表示 -->
                <template v-if="isLastPage(pageIndex)">
                  <!-- 内訳列がある場合は6列、それ以外は5列を colspan に指定 -->
                  <tr v-if="billing.tax_rate !== null && billing.tax_rate !== 0" class="billing-summary-row">
                    <td :colspan="showBreakdown ? 6 : 5">小計</td>
                    <td class="text-end">{{ formatCurrency(billing.total_amount) }}</td>
                  </tr>
                  <tr v-if="billing.tax_rate !== null && billing.tax_rate !== 0" class="billing-summary-row">
                    <td :colspan="showBreakdown ? 6 : 5">消費税 {{ billing.tax_rate }}%</td>
                    <td class="text-end">{{ formatCurrency(calculateTax()) }}</td>
                  </tr>
                  <tr class="billing-summary-row">
                    <td :colspan="showBreakdown ? 6 : 5">合計</td>
                    <td class="text-end">{{ formatCurrency(calculateNoCarryoverTotal()) }}</td>
                  </tr>
                </template>
              </tbody>
            </table>

            <!-- ✅ 振込先情報（最終ページのみ表示） -->
            <div v-if="isLastPage(pageIndex)" class="bank-info-section ms-4">
              <p class="billing-bank-info mb-2">代金は下記当社取引銀行口座にお振込下さる様お願い致します。</p>

              <!-- 振込先１(三菱UFJ銀行) -->
              <div v-if="billing.bank_display_flg_01 === true">
                <p class="billing-bank-info d-flex align-items-center mb-0 ms-2">
                  <span class="me-2">{{ billing.bank_nm_01 }}</span>
                  <span class="me-2">{{ billing.branch_nm_01 }}</span>
                  <span class="me-2">{{ billing.account_type_01 }}</span>
                  <span class="me-2">{{ billing.account_no_01 }}</span>
                </p>
              </div>

              <!-- 振込先２(りそな銀行) -->
              <div v-if="billing.bank_display_flg_02 === true">
                <p class="billing-bank-info d-flex align-items-center mb-0 ms-2">
                  <span class="me-2">{{ billing.bank_nm_02 }}</span>
                  <span class="me-2">{{ billing.branch_nm_02 }}</span>
                  <span class="me-2">{{ billing.account_type_02 }}</span>
                  <span class="me-2">{{ billing.account_no_02 }}</span>
                </p>
              </div>

              <!-- 振込先３(三井住友銀行) -->
              <div v-if="billing.bank_display_flg_03 === true">
                <p class="billing-bank-info d-flex align-items-center mb-0 ms-2">
                  <span class="me-2">{{ billing.bank_nm_03 }}</span>
                  <span class="me-2">{{ billing.branch_nm_03 }}</span>
                  <span class="me-2">{{ billing.account_type_03 }}</span>
                  <span class="me-2">{{ billing.account_no_03 }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        請求情報を読み込み中...
      </div>
    </div>
  </div>
</template>

<script>
import { savedBillingAmounts } from '@/utils/billingAmounts';
import { ref, toRefs, onMounted, computed, nextTick } from 'vue';
import { getMonthlyBilling } from '@/api/axios';
import {
  buildConsecutiveBuildingGroups,
  sortMonthlyBillingDetailsByWorkDate
} from '@/utils/monthlyBillingDetails';

const A4_HEIGHT_MM = 297;
const PDF_CONTENT_WIDTH_MM = 202;
const PAGE_BOTTOM_SAFETY_PX = 72;
const DEFAULT_ROW_HEIGHT_PX = 32;
const MAX_PAGINATION_ADJUSTMENTS = 100;

export default {
  props: {
    // 値を受け取るためのpropsを定義
    billingId: {
      type: [String, Number], // 受け取る値の型
      required: true, // 必須のpropsであることを示す
    }
  },
  setup(props) {
    const userId = ref('');
    const userName = ref('');
    const billing = ref(null);
    const pdfOutputArea = ref(null);
    const pdfContent = ref(null);
    const continuationHeaderProbe = ref(null);
    const paginationReady = ref(false);
    const paginatedBilling = ref([[]]);
    const fillerHeightByPage = ref({});

    const { billingId } = toRefs(props);

    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');
      
      try {
        const response = await getMonthlyBilling(billingId.value);
        billing.value = response.data.monthly_billing || {};
        await preparePagination();
      } catch (error) {
        console.error('請求書情報の取得に失敗しました:', error);
        paginationReady.value = true;
      }
    };

    const sortedBillingDetails = computed(() => {
      return sortMonthlyBillingDetailsByWorkDate(billing.value?.t_customer_monthly_billing_details || []);
    });

    const isLastPage = (pageIndex) => {
      return pageIndex === paginatedBilling.value.length - 1;
    };

    const parsePixelValue = (value) => {
      const parsed = parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const getOuterHeight = (element) => {
      if (!element) return 0;

      const style = window.getComputedStyle(element);
      return element.getBoundingClientRect().height
        + parsePixelValue(style.marginTop)
        + parsePixelValue(style.marginBottom);
    };

    const getA4PageHeightPx = () => {
      const probe = document.createElement('div');
      probe.style.position = 'absolute';
      probe.style.visibility = 'hidden';
      probe.style.pointerEvents = 'none';
      probe.style.width = '1px';
      probe.style.height = `${A4_HEIGHT_MM}mm`;
      probe.style.left = '-9999px';
      probe.style.top = '0';

      document.body.appendChild(probe);
      const height = probe.getBoundingClientRect().height;
      probe.remove();

      return height;
    };

    const getUsablePageHeight = () => {
      const contentWidth = pdfOutputArea.value?.getBoundingClientRect().width;
      if (contentWidth > 0) {
        return contentWidth * (A4_HEIGHT_MM / PDF_CONTENT_WIDTH_MM) - PAGE_BOTTOM_SAFETY_PX;
      }

      return getA4PageHeightPx() - PAGE_BOTTOM_SAFETY_PX;
    };

    const getTableVerticalMargins = (table) => {
      if (!table) return 0;

      const style = window.getComputedStyle(table);
      return parsePixelValue(style.marginTop) + parsePixelValue(style.marginBottom);
    };

    const sumHeights = (elements) => {
      return elements.reduce((total, element) => total + getOuterHeight(element), 0);
    };

    const measurePaginationParts = () => {
      const root = pdfContent.value;
      const firstPage = root?.querySelector('.page');
      const table = firstPage?.querySelector('.monthly-detail-data');
      const tableHead = table?.querySelector('thead');
      const header = firstPage?.querySelector('.header');
      const totalBillingAmountArea = firstPage?.querySelector('.total-billing-amount-area');
      const detailRows = Array.from(root?.querySelectorAll('.billing-detail-row') || []);
      const summaryRows = Array.from(root?.querySelectorAll('.billing-summary-row') || []);
      const bankInfoSection = root?.querySelector('.bank-info-section');
      const tableBaseHeight = getTableVerticalMargins(table) + getOuterHeight(tableHead);

      return {
        firstPageBaseHeight: getOuterHeight(header) + getOuterHeight(totalBillingAmountArea) + tableBaseHeight,
        continuationPageBaseHeight: getOuterHeight(continuationHeaderProbe.value) + tableBaseHeight,
        lastPageFooterHeight: sumHeights(summaryRows) + getOuterHeight(bankInfoSection),
        rowHeights: detailRows.map((row) => getOuterHeight(row)),
      };
    };

    const buildMeasuredPages = (details, measurements) => {
      if (details.length === 0) return [[]];

      const pages = [];
      const usablePageHeight = getUsablePageHeight();
      let detailIndex = 0;

      while (detailIndex < details.length) {
        const page = [];
        let pageHeight = pages.length === 0
          ? measurements.firstPageBaseHeight
          : measurements.continuationPageBaseHeight;

        while (detailIndex < details.length) {
          const rowHeight = measurements.rowHeights[detailIndex] || DEFAULT_ROW_HEIGHT_PX;
          const willBeLastDetail = detailIndex === details.length - 1;
          const footerHeight = willBeLastDetail ? measurements.lastPageFooterHeight : 0;
          const nextHeight = pageHeight + rowHeight + footerHeight;

          if (nextHeight <= usablePageHeight || page.length === 0) {
            page.push(details[detailIndex]);
            pageHeight += rowHeight;
            detailIndex += 1;
            continue;
          }

          break;
        }

        pages.push(page);
      }

      return pages;
    };

    const getRenderedPageHeight = (pageElement) => {
      const listContainer = pageElement?.querySelector('.list-container');
      return getOuterHeight(listContainer || pageElement);
    };

    const validateRenderedPages = async () => {
      const usablePageHeight = getUsablePageHeight();

      for (let attempt = 0; attempt < MAX_PAGINATION_ADJUSTMENTS; attempt += 1) {
        await nextTick();

        const pageElements = Array.from(pdfContent.value?.querySelectorAll('.page') || []);
        const pages = paginatedBilling.value.map((page) => page.slice());
        let adjusted = false;

        for (let pageIndex = 0; pageIndex < pageElements.length; pageIndex += 1) {
          if (getRenderedPageHeight(pageElements[pageIndex]) <= usablePageHeight) continue;
          if (!pages[pageIndex] || pages[pageIndex].length <= 1) continue;

          const movedItem = pages[pageIndex].pop();
          if (pages[pageIndex + 1]) {
            pages[pageIndex + 1].unshift(movedItem);
          } else {
            pages.splice(pageIndex + 1, 0, [movedItem]);
          }

          paginatedBilling.value = pages;
          adjusted = true;
          break;
        }

        if (!adjusted) return;
      }
    };

    const calculateFillerHeight = async () => {
      fillerHeightByPage.value = {};
      await nextTick();

      const lastPageIndex = paginatedBilling.value.length - 1;
      const lastPageElement = Array.from(pdfContent.value?.querySelectorAll('.page') || [])[lastPageIndex];
      if (!lastPageElement) return;

      const remainingHeight = getUsablePageHeight() - getRenderedPageHeight(lastPageElement);
      let fillerHeight = Math.max(0, Math.floor(remainingHeight - 12));
      if (fillerHeight < DEFAULT_ROW_HEIGHT_PX) return;

      fillerHeightByPage.value = { [lastPageIndex]: fillerHeight };
      await nextTick();

      while (
        fillerHeight >= DEFAULT_ROW_HEIGHT_PX
        && getRenderedPageHeight(Array.from(pdfContent.value?.querySelectorAll('.page') || [])[lastPageIndex]) > getUsablePageHeight()
      ) {
        fillerHeight -= 4;
        fillerHeightByPage.value = fillerHeight >= DEFAULT_ROW_HEIGHT_PX ? { [lastPageIndex]: fillerHeight } : {};
        await nextTick();
      }
    };

    const preparePagination = async () => {
      const details = sortedBillingDetails.value;
      paginationReady.value = false;
      fillerHeightByPage.value = {};
      paginatedBilling.value = [details];

      await nextTick();

      if (!pdfContent.value) {
        paginatedBilling.value = details.length > 0 ? [details] : [[]];
        paginationReady.value = true;
        return;
      }

      const measurements = measurePaginationParts();
      paginatedBilling.value = buildMeasuredPages(details, measurements);
      await validateRenderedPages();
      await calculateFillerHeight();
      paginationReady.value = true;
      await nextTick();
    };

    /**
     * ページ内の billingItem を建物ごとにグループ化。
     * ただし、追加項目（work_type === 2）は含めない。
     * 
     * @param {Array} page - 表示対象の1ページ分の請求明細データ
     * @returns {Object} 建物IDごとの出現位置と行数
     */
    const groupBuildingsInPage = (page) => {
      return buildConsecutiveBuildingGroups(page);
    };

    // breakdown（内訳）を表示すべきかを判断する computed 変数
    // billing.value の中に breakdown が null/空でないデータが1件でもあるかをチェック
    const showBreakdown = computed(() => {
      return (billing.value?.t_customer_monthly_billing_details || []).some(
        item => item.breakdown != null && item.breakdown !== ''
      );
    });
    
    // 日時フォーマット変換メソッド
    const formatDateTime = (dateTime) => {
      if (!dateTime) {
        return '';
      }
      const date = new Date(dateTime);
      return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
    };

    const formatYearMonthDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;  // 日付部分だけを取得
    };

    const formatDay = (dateString) => {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.getDate();  // 日付部分だけを取得
    };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '';
    };

    // Format currency for zero
    const formatCurrencyZero = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    const calculateTax = () => savedBillingAmounts(billing.value).tax_amount;

    // 合計金額の計算（繰越金額を除く）
    const calculateNoCarryoverTotal = () => savedBillingAmounts(billing.value).current_billing_amount;

    // 合計金額の計算（繰越金額を含む）
    const calculateGrandTotal = () => savedBillingAmounts(billing.value).billing_total_amount;

    onMounted(() => {
      fetchWorkScheduleDatas();
    });

    return {
      pdfOutputArea,
      pdfContent,
      continuationHeaderProbe,
      billing,
      paginatedBilling,
      fillerHeightByPage,
      paginationReady,
      isLastPage,
      groupBuildingsInPage,
      showBreakdown,
      formatDateTime,
      formatYearMonthDay,
      formatDay,
      formatCurrency,
      formatCurrencyZero,
      calculateTax,
      calculateNoCarryoverTotal,
      calculateGrandTotal
    };
  }
};
</script>

<style scoped>
.pdf-output-area {
  width: 202mm;
  max-width: 100%;
  margin: 0 auto;
}

.page {
  width: 100%;
}

.list-container {
  width: 100%;
  margin: 0 auto;
}

.pdf-output-area.is-measuring {
  position: absolute;
  visibility: hidden;
  left: 0;
  top: 0;
  width: 202mm;
  max-width: none;
  pointer-events: none;
}

.measurement-probe {
  position: absolute;
  visibility: hidden;
  left: -9999px;
  top: 0;
  width: 100%;
  pointer-events: none;
}

.monthly-detail-data tr.billing-detail-row,
.monthly-detail-data tr.billing-summary-row {
  break-inside: avoid;
  page-break-inside: avoid;
}

.header h4.billing-dear-nm span {
  font-size: 18px;
  font-weight: bold;
  padding: 4px 8px 4px 0;
  text-decoration-line: underline;
  text-decoration-thickness: 2px;
  text-underline-offset: 8px;
}

.header .page-no{
  font-size: 12px;
}

.header .billing-no{
  font-size: 12px;
}

.header p.biiling-dt{
  font-size: 14px;
  font-weight: bold;
  padding-right: 8px;
}

.header .middle-title .billing-greeting,
.header .middle-title .billing-company-nm,
.header .middle-title .billing-company-representative,
.header .middle-title .billing-company-address {
  font-weight: bold;
}

.header .billing-company-nm {
  line-height: 0.4;
  font-family: 'Noto Sans JP', 'MS Gothic', sans-serif;
}
.header .billing-company-representative,
.header .billing-company-address,
.header .text-end p {
  margin-left: auto;
  margin-right: 0;
  margin-bottom: 0;
  display: block;
  font-family: 'Noto Sans JP', 'MS Gothic', sans-serif;
}

.header .billing-company-nm::after{
  content: '';
  display: inline-block;
  width: 100%;
}

.header .middle-title .billing-greeting{
  font-size: 16px;
  margin-left: 20px;
}
.header .billing-company-nm { font-size: 20px; }
.header .billing-company-representative { font-size: 14px; }
.header .billing-company-address { font-size: 14px; }
.header .company-regist-no { font-size: 14px; }

.total-billing-amount-area .total-billing-amount{
  display: inline-block;
  border-bottom: 4px solid #000;
}

.monthly-detail-data {
  margin: 12px auto;
  width: 100%;
  border-collapse: collapse;
}

.monthly-detail-data th, 
.monthly-detail-data td {
  border-bottom: 1px solid #000;
  border-right: 1px solid #000;
  padding: 4px 8px;
  text-align: center;
  font-size: 12px;
  height: 32px;
  line-height: 1.35;
  vertical-align: top;
}

.monthly-detail-data th {
  border-top: 1px solid #000;
  vertical-align: middle;
}

.monthly-detail-data td {
  background-color: #fff;
}

.monthly-detail-data th:first-child,
.monthly-detail-data td:first-child{
  border-left: 1px solid #000;
}

.monthly-detail-data td.building-nm-cell {
  min-width: 170px;
}

.monthly-detail-data tr.billing-filler-row td.filler-grid-cell {
  padding-top: 0;
  padding-bottom: 0;
  background-image: repeating-linear-gradient(
    to bottom,
    transparent 0,
    transparent 31px,
    #000 31px,
    #000 32px
  );
}

.monthly-detail-data th.month-day-width{
  width: 36px;
}

.monthly-detail-data th.month-data-header {
  background-color: #f0f8ff;
  font-weight: bold;
}

.billing-bank-info{
  font-size: 14px;
  font-weight: bold;
}

.multiline-text {
  white-space: pre-line;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.page-break {
  page-break-before: always;
}
</style>
